import { auth } from "../firebase";
import { apiCall } from "./api/base";

export type SupportRequestType = "oversize_upload" | "conversion_report";

/** 백엔드에 이메일이 없을 때 본문 검증(min_length=1) 통과용. Reply-To 불가임을 message에 명시 */
const PLACEHOLDER_USER_EMAIL = "not-provided@viewr.invalid";

export interface SupportRequestDraft {
  type: SupportRequestType;
  user_uid: string | null;
  user_email: string | null;
  file_name: string;
  file_size_bytes: number | null;
  converted_size_mb: number | null;
  remaining_space_mb: number | null;
  reason: string;
  source_flow: string | null;
  requested_at: string;
  message?: string | null;
}

export interface SupportRequestResult {
  request_id: string;
  status: "accepted" | "sent" | "failed";
  sent_to: string;
  created_at: string;
}

const isAcceptedSupportStatus = (status: string): boolean =>
  status === "accepted" || status === "sent";

/**
 * POST /api/support/requests 본문 — 현재 백엔드 SupportRequestCreate(필수 필드)과 동일하게 맞춤.
 * 추가 필드(converted_size_mb 등)는 백엔드에서 허용하지 않으면 422가 될 수 있으므로 message에만 포함.
 */
interface SupportRequestPayload {
  type: SupportRequestType;
  user_uid: string;
  user_email: string;
  file_name: string;
  file_extension: string;
  file_size_bytes: number;
  reason: string;
  message?: string | null;
  source: string | null;
}

const getFileExtension = (fileName: string): string => {
  const trimmed = fileName.trim();
  const lastDotIndex = trimmed.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) {
    return "";
  }

  return trimmed.slice(lastDotIndex + 1).toLowerCase();
};

const mergeDraftWithAuth = (draft: SupportRequestDraft): SupportRequestDraft => {
  const u = auth.currentUser;
  const uid = ((u?.uid ?? draft.user_uid) || "").trim() || null;
  const em = ((u?.email ?? draft.user_email) || "").trim() || null;
  return { ...draft, user_uid: uid, user_email: em };
};

const augmentMessageForSupport = (
  draft: SupportRequestDraft,
  hadRealEmail: boolean
): string | null => {
  const chunks: string[] = [];
  if (draft.message?.trim()) {
    chunks.push(draft.message.trim());
  }
  const meta: string[] = [];
  if (typeof draft.converted_size_mb === "number" && Number.isFinite(draft.converted_size_mb)) {
    meta.push(`Converted size (MB): ${draft.converted_size_mb}`);
  }
  if (
    typeof draft.remaining_space_mb === "number" &&
    Number.isFinite(draft.remaining_space_mb)
  ) {
    meta.push(`Remaining space (MB): ${draft.remaining_space_mb}`);
  }
  if (meta.length) {
    chunks.push(meta.join(". "));
  }
  if (!hadRealEmail) {
    chunks.push(
      "Note: No email is available on the signed-in account in the client; please use User UID for follow-up."
    );
  }
  const text = chunks.join("\n\n").trim();
  return text.length ? text : null;
};

const toPayload = (draft: SupportRequestDraft, apiEmail: string): SupportRequestPayload => {
  const uid = (draft.user_uid || "").trim();
  const size = draft.file_size_bytes;
  const fileSizeBytes =
    typeof size === "number" && Number.isFinite(size) && size >= 0 ? Math.floor(size) : 0;

  return {
    type: draft.type,
    user_uid: uid,
    user_email: apiEmail,
    file_name: draft.file_name,
    file_extension: getFileExtension(draft.file_name),
    file_size_bytes: fileSizeBytes,
    reason: draft.reason,
    message: draft.message ?? null,
    source: draft.source_flow ?? null,
  };
};

export const submitSupportRequest = async (
  draft: SupportRequestDraft
): Promise<SupportRequestResult> => {
  const merged = mergeDraftWithAuth(draft);
  if (!merged.user_uid) {
    throw new Error("You must be signed in to send a support request.");
  }

  const hadRealEmail = Boolean(merged.user_email);
  const apiEmail = hadRealEmail ? merged.user_email! : PLACEHOLDER_USER_EMAIL;
  const augmentedMessage = augmentMessageForSupport(merged, hadRealEmail);
  const forPayload: SupportRequestDraft = { ...merged, message: augmentedMessage };

  const response = (await apiCall("/api/support/requests", {
    method: "POST",
    body: JSON.stringify(toPayload(forPayload, apiEmail)),
  })) as Partial<SupportRequestResult> | null;

  if (
    !response ||
    typeof response.request_id !== "string" ||
    typeof response.status !== "string" ||
    typeof response.sent_to !== "string" ||
    typeof response.created_at !== "string"
  ) {
    throw new Error("Support request response is invalid.");
  }

  if (!isAcceptedSupportStatus(response.status)) {
    throw new Error(
      response.status === "failed"
        ? "Could not send the report. Please try again later."
        : "Support request was not completed successfully."
    );
  }

  return {
    request_id: response.request_id,
    status: response.status as SupportRequestResult["status"],
    sent_to: response.sent_to,
    created_at: response.created_at,
  };
};

export const buildOversizeSupportRequestDraft = ({
  userUid,
  userEmail,
  fileName,
  fileSizeBytes,
  convertedSizeMB,
  remainingMB,
  sourceFlow,
  message,
}: {
  userUid?: string | null;
  userEmail?: string | null;
  fileName: string;
  fileSizeBytes?: number | null;
  convertedSizeMB?: number | null;
  remainingMB?: number | null;
  sourceFlow?: string | null;
  message?: string | null;
}): SupportRequestDraft => ({
  type: "oversize_upload",
  user_uid: userUid ?? null,
  user_email: userEmail ?? null,
  file_name: fileName,
  file_size_bytes: fileSizeBytes ?? null,
  converted_size_mb: convertedSizeMB ?? null,
  remaining_space_mb: remainingMB ?? null,
  reason: "File exceeds import capacity after conversion.",
  source_flow: sourceFlow ?? null,
  requested_at: new Date().toISOString(),
  message: message ?? null,
});

export const buildConversionSupportRequestDraft = ({
  userUid,
  userEmail,
  fileName,
  fileSizeBytes,
  sourceFlow,
  jobId,
  status,
  stage,
  eventCodes,
  objectInfo,
}: {
  userUid?: string | null;
  userEmail?: string | null;
  fileName: string;
  fileSizeBytes?: number | null;
  sourceFlow?: string | null;
  jobId?: string | null;
  status: "warning" | "failed";
  stage?: string | null;
  eventCodes?: string[];
  objectInfo?: string | null;
}): SupportRequestDraft => {
  const details = [
    `Result: ${status}`,
    stage ? `Stopped stage: ${stage}` : null,
    jobId ? `Conversion job: ${jobId}` : null,
    eventCodes?.length
      ? `Conversion log codes: ${Array.from(new Set(eventCodes)).join(", ")}`
      : "Conversion log codes: not available",
    objectInfo ? `Object info: ${objectInfo}` : "Object info: not available",
  ].filter((value): value is string => Boolean(value));

  return {
    type: "conversion_report",
    user_uid: userUid ?? null,
    user_email: userEmail ?? null,
    file_name: fileName,
    file_size_bytes: fileSizeBytes ?? null,
    converted_size_mb: null,
    remaining_space_mb: null,
    reason: "Conversion result needs support review.",
    source_flow: sourceFlow ?? null,
    requested_at: new Date().toISOString(),
    message: details.join("\n"),
  };
};
